import * as React from "react";
import PropTypes from 'prop-types';
import { Check, ChevronsUpDown, Loader2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import axios from "axios";
import { API } from "@/config";

/**
 * Searchable Patient Combobox with global search support
 * @param {Array} patients - List of local patient objects
 * @param {string} value - Current selected patient ID
 * @param {Function} onChange - Callback when selection changes (receives patient id, and optionally patient object if from server)
 * @param {string} placeholder - Placeholder text
 * @param {string} token - Auth token for server search
 * @param {boolean} enableGlobalSearch - Enable server-side search for patients not in local list
 * @param {Function} onPatientFromServer - Callback when patient is selected from server (receives full patient object)
 */
export function PatientCombobox({
    patients = [],
    value,
    onChange,
    placeholder = "Select patient...",
    token,
    enableGlobalSearch = false,
    onPatientFromServer,
    onAddNewPatient
}) {
    const [open, setOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const [serverResults, setServerResults] = React.useState([]);
    const [isSearching, setIsSearching] = React.useState(false);

    // Debounced server search
    React.useEffect(() => {
        if (!enableGlobalSearch || !token || searchQuery.length < 2) {
            setServerResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            setIsSearching(true);
            try {
                const response = await axios.get(`${API}/patients/search`, {
                    params: { q: searchQuery },
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Filter out patients already in local list
                const localIds = new Set(patients.map(p => p.id));
                const newResults = response.data.filter(p => !localIds.has(p.id));
                setServerResults(newResults);
            } catch (error) {
                console.error("Search error:", error);
                setServerResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery, enableGlobalSearch, token, patients]);

    const selectedPatient = React.useMemo(() => {
        const local = patients.find((p) => p.id === value);
        if (local) return local;
        return serverResults.find((p) => p.id === value);
    }, [patients, serverResults, value]);

    // Filter local patients based on search query (when enableGlobalSearch is on, we need custom filtering)
    const filteredLocalPatients = React.useMemo(() => {
        if (!enableGlobalSearch || !searchQuery) return patients;
        const query = searchQuery.toLowerCase();
        return patients.filter(p =>
            p.name?.toLowerCase().includes(query) ||
            p.phone?.includes(searchQuery) // Don't lowercase phone since it has +
        );
    }, [patients, searchQuery, enableGlobalSearch]);



    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between"
                    data-testid="patient-combobox-trigger"
                >
                    {selectedPatient ? (
                        <span className="truncate">
                            {selectedPatient.name} - {selectedPatient.phone}
                        </span>
                    ) : (
                        <span className="text-muted-foreground">{placeholder}</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={!enableGlobalSearch}>
                    <CommandInput
                        placeholder={enableGlobalSearch ? "Search by name or phone..." : "Search name or phone..."}
                        data-testid="patient-search-input"
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                    />
                    <CommandList>
                        {isSearching ? (
                            <div className="py-6 text-center text-sm">
                                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                                Searching...
                            </div>
                        ) : (
                            <>
                                <CommandEmpty>
                                    {enableGlobalSearch && searchQuery.length < 2
                                        ? "Type at least 2 characters to search..."
                                        : "No patient found."
                                    }
                                </CommandEmpty>
                                {filteredLocalPatients.length > 0 && (
                                    <CommandGroup heading={enableGlobalSearch ? "Your Patients" : undefined}>
                                        {filteredLocalPatients.map((patient) => (
                                            <CommandItem
                                                key={patient.id}
                                                value={`${patient.name} ${patient.phone} ${patient.id}`}
                                                onSelect={() => {
                                                    onChange(patient.id === value ? "" : patient.id);
                                                    setOpen(false);
                                                }}
                                                data-testid={`patient-option-${patient.id}`}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        value === patient.id ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                <div className="flex flex-col">
                                                    <span>{patient.name}</span>
                                                    <span className="text-xs text-muted-foreground">{patient.phone}</span>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                )}
                                {serverResults.length > 0 && (
                                    <CommandGroup heading="Other Patients">
                                        {serverResults.map((patient) => (
                                            <CommandItem
                                                key={patient.id}
                                                value={`${patient.name} ${patient.phone} ${patient.id}`}
                                                onSelect={() => {
                                                    if (onPatientFromServer) {
                                                        onPatientFromServer(patient);
                                                    }
                                                    onChange(patient.id);
                                                    setOpen(false);
                                                }}
                                                data-testid={`patient-option-${patient.id}`}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        value === patient.id ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                <div className="flex flex-col">
                                                    <span>{patient.name}</span>
                                                    <span className="text-xs text-muted-foreground">
                                                        {patient.phone}
                                                        <span className="ml-2 text-blue-500">(Global)</span>
                                                    </span>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                )}
                            </>
                        )}
                    </CommandList>
                    {onAddNewPatient && (
                        <div
                            className="border-t border-slate-200 p-1"
                            data-testid="add-new-patient-action"
                        >
                            <button
                                type="button"
                                className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-slate-100 text-blue-600 font-medium transition-colors"
                                onClick={() => {
                                    setOpen(false);
                                    onAddNewPatient();
                                }}
                                data-testid="add-new-patient-button"
                            >
                                <Plus className="h-4 w-4" />
                                Add New Patient
                            </button>
                        </div>
                    )}
                </Command>
            </PopoverContent>
        </Popover>
    );
}

PatientCombobox.propTypes = {
    patients: PropTypes.array,
    value: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    token: PropTypes.string,
    enableGlobalSearch: PropTypes.bool,
    onPatientFromServer: PropTypes.func,
    onAddNewPatient: PropTypes.func
};
